// Indian number formatting helpers

export function formatINR(amount) {
  if (amount === null || amount === undefined || isNaN(amount)) return "—";
  const n = Number(amount);
  if (n >= 10000000) return `₹ ${(n / 10000000).toFixed(n % 10000000 === 0 ? 0 : 2)} Cr`;
  if (n >= 100000) return `₹ ${(n / 100000).toFixed(n % 100000 === 0 ? 0 : 2)} L`;
  if (n >= 1000) return `₹ ${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)} K`;
  return `₹ ${n.toLocaleString("en-IN")}`;
}

export function formatPrice(price, txnType) {
  const base = formatINR(price);
  return txnType === "rent" ? `${base} /mo` : base;
}

// Badge shown on property listings (seller side).
export function propertyTxnLabel(txnType) {
  if (txnType === "rent") return "For Rent";
  if (txnType === "lease") return "For Lease";
  return "For Sale"; // "sale"
}

// Short label shown on buyer requirements (buyer side).
export function requirementTxnLabel(txnType) {
  if (txnType === "rent") return "Rent";
  if (txnType === "lease") return "Lease";
  if (txnType === "sell") return "Sell";
  return "Buy"; // "sale"
}

const UNIT_LABEL = { sqft: "sq.ft", sqm: "sq.m", cents: "cents", acres: "acres" };
export function formatArea(area, unit) {
  if (area === null || area === undefined) return "—";
  const n = Number(area);
  if (unit === "acres") {
    const acres = Math.floor(n + 1e-9);
    const cents = Math.round((n - acres) * 100);
    if (acres === 0 && cents === 0) return "0 Cents";
    if (acres === 0) return `${cents} ${cents === 1 ? "Cent" : "Cents"}`;
    if (cents === 0) return `${acres} ${acres === 1 ? "Acre" : "Acres"}`;
    return `${acres} ${acres === 1 ? "Acre" : "Acres"} ${cents} ${cents === 1 ? "Cent" : "Cents"}`;
  }
  if (unit === "cents") {
    const nInt = Math.round(n * 100) / 100;
    if (nInt >= 100) {
      const acres = Math.floor(nInt / 100);
      const cents = Math.round(nInt - acres * 100);
      if (cents === 0) return `${acres} ${acres === 1 ? "Acre" : "Acres"}`;
      return `${acres} ${acres === 1 ? "Acre" : "Acres"} ${cents} ${cents === 1 ? "Cent" : "Cents"}`;
    }
    return `${nInt} ${nInt === 1 ? "Cent" : "Cents"}`;
  }
  const u = UNIT_LABEL[unit] || unit;
  return `${n.toLocaleString("en-IN")} ${u}`;
}

// Approximate conversion helpers (match backend _to_sqft)
export const TO_SQFT = { sqft: 1, sqm: 10.7639, cents: 435.6, acres: 43560 };
export function toSqft(value, unit) {
  const v = Number(value);
  if (isNaN(v)) return null;
  return v * (TO_SQFT[unit] || 1);
}

export function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const s = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  return `${mo}mo ago`;
}

// WhatsApp deep-link
export function waLink(number, message = "") {
  const clean = String(number || "").replace(/\D/g, "");
  const enc = encodeURIComponent(message);
  return `https://wa.me/${clean}${message ? `?text=${enc}` : ""}`;
}

// Capitalize the first letter of each word — used for property descriptions
// so free-form text still reads naturally without shouting.
export function toTitleCase(text) {
  if (!text) return text;
  return String(text).replace(/(^|\s)([a-z])/g, (_, sp, ch) => sp + ch.toUpperCase());
}


/**
 * Resolve the public contact surface for a property.
 *
 * Rule: when an admin has curated `listed_by` (posting on behalf of a
 * 3rd-party client), the admin's own account details must NEVER be exposed
 * as the seller. If the admin didn't enter client contact fields we return
 * empty strings so the UI shows "contact via AcresDeal" instead of leaking
 * the admin's mobile/whatsapp/email.
 *
 * Priority for regular listings (no `listed_by`):
 *   property.owner_* → property.broker.* → empty
 *
 * Returns: { name, phone, whatsapp, email, profileHref, hasOverride }
 */
export function resolvePropertyContact(property) {
  if (!property) return { name: "", phone: "", whatsapp: "", email: "", profileHref: null, hasOverride: false };
  const b = property.broker || {};
  const hasOverride = !!(property.listed_by && String(property.listed_by).trim());
  const name = hasOverride
    ? String(property.listed_by).trim()
    : (property.owner_name || b.name || "AcresDeal");
  const phone = property.owner_phone || (hasOverride ? "" : (b.mobile || ""));
  const whatsapp = property.owner_whatsapp || (hasOverride ? "" : (b.whatsapp || phone));
  const email = property.owner_email || (hasOverride ? "" : (b.email || ""));
  // Never link to admin's profile when the listing is on behalf of a client.
  const profileHref = hasOverride ? null : (b.id ? `/u/${b.id}` : null);
  return { name, phone, whatsapp, email, profileHref, hasOverride };
}
